import { useEffect, useMemo, useRef, useState } from "react";

function getImageId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      resolve({
        image,
        url,
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`No se pudo cargar ${file.name}`));
    };

    image.src = url;
  });
}

function downloadCanvasAsPng(canvas, fileName) {
  if (!canvas) return;

  const link = document.createElement("a");

  link.download = fileName || "spritesheet.png";
  link.href = canvas.toDataURL("image/png");
  link.click();
}

function SpriteSheetGeneratorTool() {
  const canvasRef = useRef(null);

  const [sprites, setSprites] = useState([]);
  const [columns, setColumns] = useState(6);
  const [cellWidth, setCellWidth] = useState(512);
  const [cellHeight, setCellHeight] = useState(512);
  const [padding, setPadding] = useState(0);
  const [background, setBackground] = useState("transparent");
  const [fitMode, setFitMode] = useState("contain");
  const [fileName, setFileName] = useState("spritesheet.png");
  const [loading, setLoading] = useState(false);

  const rows = useMemo(() => {
    if (sprites.length === 0) return 0;

    return Math.ceil(sprites.length / Math.max(1, Number(columns) || 1));
  }, [sprites.length, columns]);

  const sheetWidth = useMemo(() => {
    return Math.max(1, Number(columns) || 1) * Number(cellWidth || 1);
  }, [columns, cellWidth]);

  const sheetHeight = useMemo(() => {
    return rows * Number(cellHeight || 1);
  }, [rows, cellHeight]);

  async function handleUploadImages(event) {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) return;

    try {
      setLoading(true);

      const loadedSprites = await Promise.all(
        files.map(async (file) => {
          const loaded = await loadImageFromFile(file);

          return {
            id: getImageId(),
            name: file.name,
            file,
            image: loaded.image,
            previewUrl: loaded.url,
            width: loaded.width,
            height: loaded.height,
          };
        })
      );

      setSprites((prev) => [...prev, ...loadedSprites]);
    } catch (error) {
      console.error(error);
      alert(error.message || "No se pudieron cargar las imágenes.");
    } finally {
      event.target.value = "";
      setLoading(false);
    }
  }

  function removeSprite(spriteId) {
    setSprites((prev) => {
      const spriteToRemove = prev.find((item) => item.id === spriteId);

      if (spriteToRemove?.previewUrl) {
        URL.revokeObjectURL(spriteToRemove.previewUrl);
      }

      return prev.filter((item) => item.id !== spriteId);
    });
  }

  function clearSprites() {
    const confirmed = window.confirm("¿Eliminar todas las imágenes cargadas?");

    if (!confirmed) return;

    sprites.forEach((sprite) => {
      if (sprite.previewUrl) {
        URL.revokeObjectURL(sprite.previewUrl);
      }
    });

    setSprites([]);
  }

  function moveSprite(spriteId, direction) {
    setSprites((prev) => {
      const currentIndex = prev.findIndex((item) => item.id === spriteId);

      if (currentIndex === -1) return prev;

      const nextIndex = currentIndex + direction;

      if (nextIndex < 0 || nextIndex >= prev.length) return prev;

      const copy = [...prev];
      const [removed] = copy.splice(currentIndex, 1);
      copy.splice(nextIndex, 0, removed);

      return copy;
    });
  }

  function drawSprite(ctx, sprite, cellX, cellY, targetW, targetH) {
    const innerX = cellX + Number(padding || 0);
    const innerY = cellY + Number(padding || 0);
    const innerW = Math.max(1, targetW - Number(padding || 0) * 2);
    const innerH = Math.max(1, targetH - Number(padding || 0) * 2);

    if (fitMode === "stretch") {
      ctx.drawImage(sprite.image, innerX, innerY, innerW, innerH);
      return;
    }

    if (fitMode === "cover") {
      const scale = Math.max(innerW / sprite.width, innerH / sprite.height);
      const drawW = sprite.width * scale;
      const drawH = sprite.height * scale;
      const drawX = innerX + (innerW - drawW) / 2;
      const drawY = innerY + (innerH - drawH) / 2;

      ctx.save();
      ctx.beginPath();
      ctx.rect(innerX, innerY, innerW, innerH);
      ctx.clip();
      ctx.drawImage(sprite.image, drawX, drawY, drawW, drawH);
      ctx.restore();
      return;
    }

    const scale = Math.min(innerW / sprite.width, innerH / sprite.height);
    const drawW = sprite.width * scale;
    const drawH = sprite.height * scale;
    const drawX = innerX + (innerW - drawW) / 2;
    const drawY = innerY + (innerH - drawH) / 2;

    ctx.drawImage(sprite.image, drawX, drawY, drawW, drawH);
  }

  function renderSpriteSheet() {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const cleanColumns = Math.max(1, Number(columns) || 1);
    const cleanCellWidth = Math.max(1, Number(cellWidth) || 1);
    const cleanCellHeight = Math.max(1, Number(cellHeight) || 1);
    const cleanRows = Math.ceil(sprites.length / cleanColumns);

    canvas.width = cleanColumns * cleanCellWidth;
    canvas.height = Math.max(1, cleanRows * cleanCellHeight);

    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (background !== "transparent") {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    sprites.forEach((sprite, index) => {
      const column = index % cleanColumns;
      const row = Math.floor(index / cleanColumns);

      const cellX = column * cleanCellWidth;
      const cellY = row * cleanCellHeight;

      drawSprite(ctx, sprite, cellX, cellY, cleanCellWidth, cleanCellHeight);
    });
  }

  useEffect(() => {
    renderSpriteSheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprites, columns, cellWidth, cellHeight, padding, background, fitMode]);

  useEffect(() => {
    return () => {
      sprites.forEach((sprite) => {
        if (sprite.previewUrl) {
          URL.revokeObjectURL(sprite.previewUrl);
        }
      });
    };
  }, [sprites]);

  return (
    <div className="spritesheet-tool">
      <section className="tools-panel">
        <div className="tools-panel__header">
          <div>
            <span className="section-eyebrow">Sprites</span>
            <h3>SpriteSheet Generator</h3>
            <p>
              Sube imágenes, define el tamaño de celda, previsualiza el
              spritesheet y descárgalo como PNG.
            </p>
          </div>

          <div className="tools-output-actions">
            <label className="secondary-button spritesheet-upload-button">
              Subir imágenes
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                onChange={handleUploadImages}
                disabled={loading}
              />
            </label>

            <button
              type="button"
              className="primary-action-button"
              onClick={() => downloadCanvasAsPng(canvasRef.current, fileName)}
              disabled={sprites.length === 0}
            >
              Descargar PNG
            </button>

            <button
              type="button"
              className="small-button small-button--danger"
              onClick={clearSprites}
              disabled={sprites.length === 0}
            >
              Limpiar
            </button>
          </div>
        </div>

        <div className="spritesheet-summary-grid">
          <article>
            <span>🖼️</span>
            <div>
              <strong>{sprites.length}</strong>
              <small>Imágenes</small>
            </div>
          </article>

          <article>
            <span>↔️</span>
            <div>
              <strong>{sheetWidth}px</strong>
              <small>Ancho sheet</small>
            </div>
          </article>

          <article>
            <span>↕️</span>
            <div>
              <strong>{sheetHeight}px</strong>
              <small>Alto sheet</small>
            </div>
          </article>

          <article>
            <span>🔲</span>
            <div>
              <strong>
                {columns} × {rows}
              </strong>
              <small>Grid</small>
            </div>
          </article>
        </div>

        <div className="spritesheet-config-card">
          <div className="form-block-title">
            <h3>Configuración</h3>
            <p>Define cómo se genera el spritesheet.</p>
          </div>

          <div className="spritesheet-config-grid">
            <label>
              Columnas
              <input
                type="number"
                min="1"
                value={columns}
                onChange={(e) => setColumns(e.target.value)}
              />
            </label>

            <label>
              Ancho celda
              <input
                type="number"
                min="1"
                value={cellWidth}
                onChange={(e) => setCellWidth(e.target.value)}
              />
            </label>

            <label>
              Alto celda
              <input
                type="number"
                min="1"
                value={cellHeight}
                onChange={(e) => setCellHeight(e.target.value)}
              />
            </label>

            <label>
              Padding
              <input
                type="number"
                min="0"
                value={padding}
                onChange={(e) => setPadding(e.target.value)}
              />
            </label>

            <label>
              Ajuste imagen
              <select
                value={fitMode}
                onChange={(e) => setFitMode(e.target.value)}
              >
                <option value="contain">Contain</option>
                <option value="cover">Cover</option>
                <option value="stretch">Stretch</option>
              </select>
            </label>

            <label>
              Fondo
              <select
                value={background}
                onChange={(e) => setBackground(e.target.value)}
              >
                <option value="transparent">Transparente</option>
                <option value="#ffffff">Blanco</option>
                <option value="#000000">Negro</option>
                <option value="#f3f4f6">Gris claro</option>
              </select>
            </label>

            <label>
              Nombre archivo
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="spritesheet.png"
              />
            </label>
          </div>
        </div>

        <div className="spritesheet-layout">
          <section className="spritesheet-list-panel">
            <div className="spritesheet-panel-title">
              <strong>Imágenes</strong>
              <span>Orden de dibujo</span>
            </div>

            {sprites.length === 0 ? (
              <p className="empty-state">Sube imágenes para empezar.</p>
            ) : (
              <div className="spritesheet-image-list">
                {sprites.map((sprite, index) => (
                  <article className="spritesheet-image-item" key={sprite.id}>
                    <img src={sprite.previewUrl} alt={sprite.name} />

                    <div>
                      <strong>{sprite.name}</strong>
                      <span>
                        #{index + 1} · {sprite.width}×{sprite.height}px
                      </span>
                    </div>

                    <div className="spritesheet-item-actions">
                      <button
                        type="button"
                        className="small-button"
                        onClick={() => moveSprite(sprite.id, -1)}
                        disabled={index === 0}
                      >
                        ↑
                      </button>

                      <button
                        type="button"
                        className="small-button"
                        onClick={() => moveSprite(sprite.id, 1)}
                        disabled={index === sprites.length - 1}
                      >
                        ↓
                      </button>

                      <button
                        type="button"
                        className="small-button small-button--danger"
                        onClick={() => removeSprite(sprite.id)}
                      >
                        ×
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="spritesheet-preview-panel">
            <div className="spritesheet-panel-title">
              <strong>Preview</strong>
              <span>
                {sheetWidth}×{sheetHeight}px
              </span>
            </div>

            <div className="spritesheet-canvas-wrap">
              <canvas ref={canvasRef} className="spritesheet-canvas" />
            </div>
          </section>
        </div>
      </section>
    </div>
  );
}

export default SpriteSheetGeneratorTool;